import React from "react";
import { Link } from "zudoku/components";
import { brand } from "../../brands/index.js";
import { t } from "../../i18n/index.js";

const isMinimal = brand.theme.landingStyle === "minimal";

type Feature = {
  icon: React.ReactNode;
  accent: string;
  title: string;
  desc: string;
  tags: readonly string[];
  href: string;
};

function getFeatures(): Feature[] {
  const s = t.featureGrid;
  const links = brand.landingLinks;

  // trenz 极简：四张卡统一 neutral accent；出海匠用 4 色 accent
  const accents = isMinimal
    ? ["var(--foreground)", "var(--foreground)", "var(--foreground)", "var(--foreground)"]
    : [
        "oklch(0.62 0.2 280)",
        "oklch(0.68 0.18 320)",
        "oklch(0.7 0.15 200)",
        "oklch(0.7 0.16 40)",
      ];

  return [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18" />
          <path d="M7 14l4-4 4 4 5-5" />
        </svg>
      ),
      accent: accents[0],
      title: s.ecommerce.title,
      desc: s.ecommerce.desc,
      tags: s.ecommerce.tags,
      href: links.ecommerceProducts,
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m10 9 5 3-5 3z" />
        </svg>
      ),
      accent: accents[1],
      title: s.aiContent.title,
      desc: s.aiContent.desc,
      tags: s.aiContent.tags,
      href: links.aiVideo,
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      accent: accents[2],
      title: s.social.title,
      desc: s.social.desc,
      tags: s.social.tags,
      href: links.social,
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 11l18-5v12L3 13z" />
          <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
        </svg>
      ),
      accent: accents[3],
      title: s.ads.title,
      desc: s.ads.desc,
      tags: s.ads.tags,
      href: links.ads,
    },
  ];
}

export function FeatureGrid() {
  const features = getFeatures();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: isMinimal ? "20px" : "16px",
        margin: isMinimal ? "48px 0 80px" : "24px 0 48px",
      }}
    >
      {features.map((f) => (
        <FeatureCard key={f.title} {...f} />
      ))}
    </div>
  );
}

function FeatureCard({ icon, accent, title, desc, tags, href }: Feature) {
  if (isMinimal) return <FeatureCardMinimal icon={icon} title={title} desc={desc} tags={tags} href={href} />;

  return (
    <Link
      to={href}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: "20px 22px",
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        textDecoration: "none",
        color: "inherit",
        transition:
          "border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `0 12px 28px -14px ${accent}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "";
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          borderRadius: "calc(var(--radius) - 2px)",
          color: accent,
          background: `color-mix(in oklab, ${accent} 12%, transparent)`,
        }}
      >
        {icon}
      </div>

      <div>
        <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: 600, letterSpacing: "-0.01em", color: "var(--foreground)" }}>
          {title}
        </h3>
        <p style={{ margin: 0, fontSize: "13.5px", lineHeight: 1.6, color: "var(--muted-foreground)" }}>
          {desc}
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: "2px 8px",
              fontSize: "11.5px",
              color: "var(--muted-foreground)",
              background: "var(--muted)",
              borderRadius: "9999px",
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </Link>
  );
}

/* Trenz 极简版：外层 gradient padding 1px + 内层白卡，hover 时 gradient 加浓（"被点亮"） */
function FeatureCardMinimal({
  icon,
  title,
  desc,
  tags,
  href,
}: Omit<Feature, "accent">) {
  const gradBorderDefault =
    "linear-gradient(135deg, color-mix(in oklab, var(--brand-primary) 18%, var(--border)) 0%, color-mix(in oklab, var(--brand-accent) 15%, var(--border)) 100%)";
  const gradBorderHover =
    "linear-gradient(135deg, color-mix(in oklab, var(--brand-primary) 55%, transparent) 0%, color-mix(in oklab, var(--brand-accent) 45%, transparent) 100%)";

  return (
    <Link
      to={href}
      style={{
        position: "relative",
        display: "block",
        padding: "1px",
        borderRadius: "14px",
        background: gradBorderDefault,
        textDecoration: "none",
        color: "inherit",
        transition: "transform 180ms ease, background 220ms ease, box-shadow 220ms ease",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.background = gradBorderHover;
        e.currentTarget.style.boxShadow =
          "0 18px 40px -22px color-mix(in oklab, var(--brand-primary) 30%, transparent), 0 4px 14px -6px rgba(0, 0, 0, 0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.background = gradBorderDefault;
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)";
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          padding: "28px 26px",
          height: "100%",
          background: "var(--card)",
          borderRadius: "13px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            color: "var(--foreground)",
            background: "color-mix(in oklab, var(--muted) 80%, transparent)",
            border: "1px solid var(--border)",
          }}
        >
          {icon}
        </div>

        <div>
          <h3 style={{ margin: "0 0 8px", fontSize: "17px", fontWeight: 600, letterSpacing: "-0.015em", color: "var(--foreground)" }}>
            {title}
          </h3>
          <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.6, color: "var(--muted-foreground)" }}>
            {desc}
          </p>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "auto" }}>
          {tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: "3px 10px",
              fontSize: "11.5px",
              color: "var(--muted-foreground)",
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: "9999px",
              letterSpacing: "0.01em",
            }}
          >
            {tag}
          </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
